import { DeviceSelectionCategoryCoCo, DeviceSelectionProductTypeCoCo } from '../enums.js';
import { accessoriesMsg, computersMsg, pcsMsg, phonesMsg, t, tabletsMsg } from '../i18n';

export { DeviceSelectionCategoryCoCo } from '../enums.js';

export interface DeviceSelectionCategoryCountCoCo {
  category: DeviceSelectionCategoryCoCo;
  count: number;
}

export interface DeviceSelectionCategoryConfigCoCo {
  category: DeviceSelectionCategoryCoCo;
  icon: string;
  label: () => string;
}

// TODO: check whether categories, their icons and labels can be managed via CMS instead of being hard coded
const CATEGORY_CONFIGS: Record<DeviceSelectionCategoryCoCo, DeviceSelectionCategoryConfigCoCo> = {
  [DeviceSelectionCategoryCoCo.PHONES]: {
    category: DeviceSelectionCategoryCoCo.PHONES,
    icon: 'phone',
    label: () => t.E9IF(phonesMsg),
  },
  [DeviceSelectionCategoryCoCo.TABLETS]: {
    category: DeviceSelectionCategoryCoCo.TABLETS,
    icon: 'tablet',
    label: () => t.XS42(tabletsMsg),
  },
  [DeviceSelectionCategoryCoCo.COMPUTERS]: {
    category: DeviceSelectionCategoryCoCo.COMPUTERS,
    icon: 'laptop',
    label: () => t.MCW3(computersMsg),
  },
  [DeviceSelectionCategoryCoCo.OTHER_DEVICES]: {
    category: DeviceSelectionCategoryCoCo.OTHER_DEVICES,
    icon: 'devices',
    label: () => t.JZH3('Other devices'),
  },
  [DeviceSelectionCategoryCoCo.ACCESSORIES]: {
    category: DeviceSelectionCategoryCoCo.ACCESSORIES,
    icon: 'headphones',
    label: () => t.J954(accessoriesMsg),
  },
  [DeviceSelectionCategoryCoCo.ACCESSORIES_AND_OTHER_DEVICES]: {
    category: DeviceSelectionCategoryCoCo.ACCESSORIES_AND_OTHER_DEVICES,
    icon: 'headphones',
    label: () => t.XW97('Accessories and other devices'),
  },
};

export const getDeviceSelectionCategoryConfig = (category: DeviceSelectionCategoryCoCo) => CATEGORY_CONFIGS[category];

export const formatDeviceSelectionCount = (label: string, count: number) => `${label}: ${count} ${t.B3MG(pcsMsg)}`;

export interface DeviceSelectionCategoryGroupCoCo {
  id: string;
  heading: () => string;
  categories: DeviceSelectionCategoryConfigCoCo[];
}

const COMMON_CATEGORIES = [
  DeviceSelectionCategoryCoCo.PHONES,
  DeviceSelectionCategoryCoCo.TABLETS,
  DeviceSelectionCategoryCoCo.COMPUTERS,
  DeviceSelectionCategoryCoCo.OTHER_DEVICES,
  DeviceSelectionCategoryCoCo.ACCESSORIES,
];

// TODO: check whether the category groups per product type can be managed via CMS instead of being hard coded
const CATEGORY_GROUPS_BY_PRODUCT_TYPE: Record<DeviceSelectionProductTypeCoCo, DeviceSelectionCategoryGroupCoCo[]> = {
  [DeviceSelectionProductTypeCoCo.EPP]: [
    {
      id: 'epp-devices',
      heading: () => t.KZ5M('Elisa Palvelupäätelaite devices'),
      categories: COMMON_CATEGORIES.map(getDeviceSelectionCategoryConfig),
    },
    {
      id: 'one-time-payment-devices',
      heading: () => t.CYTU('One-time payment devices'),
      categories: [getDeviceSelectionCategoryConfig(DeviceSelectionCategoryCoCo.ACCESSORIES_AND_OTHER_DEVICES)],
    },
  ],
  [DeviceSelectionProductTypeCoCo.PURCHASED]: [
    {
      id: 'purchased-devices',
      heading: () => t.QMXU('Purchased devices'),
      categories: COMMON_CATEGORIES.map(getDeviceSelectionCategoryConfig),
    },
  ],
};

// Flattened once, as the groups are static
const CATEGORIES_BY_PRODUCT_TYPE = Object.fromEntries(
  Object.entries(CATEGORY_GROUPS_BY_PRODUCT_TYPE).map(([productType, groups]) => [
    productType,
    groups.flatMap(({ categories }) => categories),
  ])
) as Record<DeviceSelectionProductTypeCoCo, DeviceSelectionCategoryConfigCoCo[]>;

export const getDeviceSelectionCategoryGroups = (
  productType?: DeviceSelectionProductTypeCoCo
): DeviceSelectionCategoryGroupCoCo[] => (productType ? CATEGORY_GROUPS_BY_PRODUCT_TYPE[productType] : []);

export const getDeviceSelectionCategories = (
  productType?: DeviceSelectionProductTypeCoCo
): DeviceSelectionCategoryConfigCoCo[] => (productType ? CATEGORIES_BY_PRODUCT_TYPE[productType] : []);

export const getFirstDeviceSelectionCategory = (
  productType?: DeviceSelectionProductTypeCoCo
): DeviceSelectionCategoryCoCo | undefined => getDeviceSelectionCategories(productType)[0]?.category;
